import { Link } from "@tanstack/react-router";
import { clsx } from "clsx";

/**
 * Filter control for search-result scope. Three options that map to the
 * V1 auth scope model from spec §15:
 *
 *   * `all`     — every record visible to the caller (commons + their
 *                 own contributions + their org's `org_private` when in
 *                 an org). Default; no filter applied server-side.
 *   * `commons` — only the curated 240 commons documents. Filters out
 *                 user uploads.
 *   * `org`     — restricts to org-relevant: commons + the caller's
 *                 contributions + org_private. Useful when an
 *                 organization wants a focused view.
 *
 * The toggle is URL-driven via `?scope=…`. Anonymous and individual
 * users still see every option — the backend's `VisibleScope` predicate
 * (Zeke's `app/auth/visibility.py`) clamps results to what the caller
 * is actually allowed to see, so picking `org` as an anonymous user
 * just returns commons. The toggle reads as an affordance for
 * organizations evaluating QM more than as a functional filter for
 * anonymous demo flows.
 *
 * V1 demo reality: the backend `/search` endpoint does not yet read the
 * `scope` query param (lands with the W4 score-combiner pair-week + the
 * scope-field rollout). Until then, all three options return the same
 * results — the toggle ships as a wired UI affordance.
 *
 * Anatomy mirrors `ModeToggle` deliberately so the two segmented
 * controls read as siblings when they sit in the same row.
 */
export type ScopeMode = "all" | "commons" | "org";

export const SCOPE_MODES: readonly ScopeMode[] = ["all", "commons", "org"] as const;

const LABEL: Record<ScopeMode, string> = {
  all: "All sources",
  commons: "Commons",
  org: "My organization",
};

export interface ScopeToggleProps {
  /** Current search query (preserved on the link target). */
  query: string;
  /** Page mode (`search` / `absence`) preserved on the link target. */
  mode: "search" | "absence";
  /** Active scope; defaults to `all` when caller has no URL state to read. */
  scope: ScopeMode;
  /** Preserved across the toggle so an entity-scoped query stays scoped. */
  entity?: string;
  /** Preserved across the toggle so an institution-scoped query stays scoped. */
  institution?: string;
  /** Subset of options to render. Defaults to all three. Future PR can
   * narrow this from auth context — anonymous users get `["all",
   * "commons"]`, org users get the full set. */
  available?: readonly ScopeMode[];
}

export function ScopeToggle({
  query,
  mode,
  scope,
  entity,
  institution,
  available = SCOPE_MODES,
}: ScopeToggleProps) {
  return (
    <nav
      aria-label="Source scope"
      className="inline-flex rounded-full bg-surface-sunken p-1"
    >
      {available.map((target) => (
        <ScopeLink
          key={target}
          query={query}
          mode={mode}
          target={target}
          current={scope}
          entity={entity}
          institution={institution}
        />
      ))}
    </nav>
  );
}

function ScopeLink({
  query,
  mode,
  target,
  current,
  entity,
  institution,
}: {
  query: string;
  mode: "search" | "absence";
  target: ScopeMode;
  current: ScopeMode;
  entity?: string;
  institution?: string;
}) {
  const active = current === target;
  return (
    <Link
      to="/search"
      search={{ q: query, mode, scope: target, entity, institution }}
      aria-current={active ? "page" : undefined}
      className={clsx(
        "rounded-full px-4 py-1.5 text-meta font-medium transition",
        active
          ? "bg-surface-card text-ink-primary shadow-sm"
          : "text-ink-secondary hover:text-ink-primary",
      )}
    >
      {LABEL[target]}
    </Link>
  );
}
